import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'

import ScrollTable from 'components/global/CUK/ScrollTable'
import TableSortLabel from 'components/global/TableSort'
import TemporaryIcon from 'components/global/TemporaryIcon'

import TableSectionSkeleton from '../../../Skeletons/TableSectionSkeleton'
import { formatBytesWithUnit } from '../../../helpers'
import useSort from '../../hooks/use-sort'
import useData from './use-data'

const TableTypes = () => {
  const [expandedTypes, setExpandedTypes] = useState<Record<string, boolean>>({
    guest: true,
    private: true,
    other: true
  })
  const sortApi = useSort()
  const {t, i18n} = useTranslation()
  const { data, loading } = useData()

  const getTypeColor = useCallback((type: string) => {
    const colorMap = {
      guest: '#EC7A74',
      private: '#4F8AA4',
      other: '#A5D9CB'
    }
    return colorMap[type] || '#cccccc'
  }, [])

  if (loading) {
    return <TableSectionSkeleton />
  }
  return (
    <Box sx={{
      width: { xs: '100%', md: '40%' },
      border: '1px solid #EFEFEF',
      borderRadius: '16px',
      p: 2,
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Typography sx={{
        fontWeight: 600,
        fontSize: '12px',
        mb: 2,
        textTransform: 'uppercase',
        textAlign: 'center'
      }}>
        {t('stats.traffic.table.type_distribution_title')}
      </Typography>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <ScrollTable height={140} shouldFetchNextPage={false}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '50%', minWidth: '140px', pl: 2 }}>
                  <TableSortLabel
                    orderName="label"
                    orderBy={sortApi?.orderBy}
                    onOrderByChanged={sortApi?.orderByChange}
                    direction={sortApi?.orderDirection}
                  >
                    {t('stats.traffic.table.headers.label_type')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '25%', minWidth: '100px', pl: 2 }}>
                  <TableSortLabel
                    orderName="traffic"
                    orderBy={sortApi?.orderBy}
                    onOrderByChanged={sortApi?.orderByChange}
                    direction={sortApi?.orderDirection}
                  >
                    {t('stats.traffic.table.headers.download')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '25%', minWidth: '80px', pl: 2 }}>
                  {t('stats.traffic.table.headers.ratio')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map(ssidsByType => {
                if (!ssidsByType.ssids.length) return null
                const isExpanded = expandedTypes[ssidsByType.type]
                const { value: totalTypeValue, unit: totalTypeUnit } = formatBytesWithUnit(ssidsByType.total, i18n.language)
                return (
                  <React.Fragment key={ssidsByType.type}>
                    <TableRow>
                      <TableCell sx={{ width: '50%', borderBottom: '1px solid #F7F7F7', pl: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setExpandedTypes(prev => ({
                                ...prev,
                                [ssidsByType.type]: !prev[ssidsByType.type]
                              }))
                            }}
                            sx={{
                              width: 20,
                              height: 20,
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease'
                            }}
                          >
                            <TemporaryIcon
                              iconlabel="chevron-down"
                              iconsize="12px"
                            />
                          </IconButton>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: getTypeColor(ssidsByType.type)
                            }}
                          />
                          <Typography sx={{ fontWeight: 500, fontSize: '12px'}}>
                            {t(`stats.legends.types.${ssidsByType.type}`)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ width: '25%', borderBottom: '1px solid #F7F7F7', pl: 2 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                          {totalTypeValue}
                          <Typography component="span" sx={{ fontSize: '10px', color: '#989898', ml: 0.5 }}>
                            {totalTypeUnit}
                          </Typography>
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '25%', borderBottom: '1px solid #F7F7F7', pl: 2 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                          {ssidsByType.percentage}%
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={3} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isExpanded}>
                          <Box sx={{
                            backgroundColor: 'white',
                            position: 'relative',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: '22px',
                              top: 0,
                              bottom: 0,
                              width: '1px',
                              backgroundColor: '#EFEFEF',
                              zIndex: 1
                            }
                          }}>
                            {sortApi.sort(ssidsByType.ssids).map((item, index) => {
                              const { value: totalItemValue, unit: totalItemUnit } = formatBytesWithUnit(item.total, i18n.language)
                              return (
                                <Box
                                  key={`${item.name}-${index}`}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '8px 16px',
                                    position: 'relative',
                                    backgroundColor: 'white',
                                    '&::before': {
                                      content: '""',
                                      position: 'absolute',
                                      left: '22px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      width: '12px',
                                      height: '1px',
                                      backgroundColor: '#EFEFEF',
                                      zIndex: 2
                                    },
                                    '&:last-child::after': {
                                      content: '""',
                                      position: 'absolute',
                                      left: '22px',
                                      top: '50%',
                                      bottom: 0,
                                      width: '1px',
                                      backgroundColor: 'white',
                                      zIndex: 3
                                    }
                                  }}
                                >
                                  <Box sx={{
                                    width: '50%',
                                    pl: 5,
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}>
                                    <Typography sx={{ fontWeight: 400, fontSize: '11px'}}>
                                      {item.name}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ width: '25%', pl: 2 }}>
                                    <Typography sx={{ fontSize: '11px'}}>
                                      {totalItemValue}
                                      <Typography component="span" sx={{ fontSize: '10px', color: '#989898', ml: 0.5 }}>
                                        {totalItemUnit}
                                      </Typography>
                                    </Typography>
                                  </Box>
                                  <Box sx={{ width: '25%', pl: 2 }}>
                                    <Typography sx={{ fontSize: '11px'}}>
                                      {item.percentage}%
                                    </Typography>
                                  </Box>
                                </Box>
                              )
                            })}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </ScrollTable>
      </Box>
    </Box>
  )
}

export default TableTypes
